import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, Home, Menu, Receipt, Search, ShoppingBag, User, Wallet, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import appIcon from "../assets/appIcon.png";

const customerLinks = [
  { label: "Home", path: "/", icon: Home },
  { label: "Products", path: "/products", icon: ShoppingBag },
  { label: "Subscription", path: "/subscription", icon: CalendarDays, protected: true },
  { label: "Basket", path: "/basket", icon: Receipt, protected: true },
  { label: "Wallet", path: "/wallet", icon: Wallet, protected: true },
  { label: "Profile", path: "/profile", icon: User, protected: true },
];

export default function UserNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [subcategoryResults, setSubcategoryResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const query = search.trim();
    if (!query) {
      setSearchResults([]);
      setSubcategoryResults([]);
      setSearching(false);
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        setSearching(true);
        const [{ data }, { data: categoryData }] = await Promise.all([
          axios.get("https://www.frendrops.in/api/products/search", {
            params: { search: query, page: 1, limit: 20, sort: "newest" },
          }),
          axios.get("https://www.frendrops.in/api/home/categories"),
        ]);
        setSearchResults(Array.isArray(data?.data?.products) ? data.data.products : []);

        const categoryPayload = categoryData?.data ?? categoryData;
        const categories = Array.isArray(categoryPayload)
          ? categoryPayload
          : categoryPayload?.categories || categoryPayload?.mainCategories || categoryPayload?.main_categories || categoryPayload?.app_categories || [];
        const normalizedQuery = query.toLowerCase();
        const matches = (Array.isArray(categories) ? categories : []).flatMap((category: any) => {
          const children = category?.subcategories || category?.subCategories || category?.sub_categories || [];
          return (Array.isArray(children) ? children : []).filter((item: any) =>
            String(item?.name || "").toLowerCase().includes(normalizedQuery)
          );
        });
        setSubcategoryResults(matches);
      } catch {
        setSearchResults([]);
        setSubcategoryResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);

    return () => window.clearTimeout(timer);
  }, [search]);

  const goTo = (path: string, protectedPage?: boolean) => {
    setMobileOpen(false);
    if (protectedPage && !localStorage.getItem("token")) {
      navigate("/login", { state: { from: { pathname: path } } });
      return;
    }
    navigate(path);
    if (path === "/") {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
    }
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-violet-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between">
          <button type="button" onClick={() => goTo("/")} className="flex items-center gap-3">
            <img src={appIcon} alt="FrendRops" className="w-10 h-10 rounded-xl" />
            <span className="text-white text-xl sm:text-2xl font-bold">FrendRops</span>
          </button>

          <div className="hidden md:flex items-center gap-2">
            {customerLinks.map(({ label, path, icon: Icon, protected: protectedPage }) => {
              const active = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
              return (
                <button
                  type="button"
                  key={path}
                  onClick={() => goTo(path, protectedPage)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition ${
                    active ? "bg-white text-violet-700" : "text-white hover:bg-violet-600"
                  }`}
                >
                  <Icon size={18} />
                  {label}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((value) => !value)}
            className="md:hidden text-white p-2 rounded-lg hover:bg-violet-600"
            aria-label="Toggle customer menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden border-t border-violet-500 bg-violet-700 px-4 py-3 space-y-1">
            {customerLinks.map(({ label, path, icon: Icon, protected: protectedPage }) => {
              const active = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
              return (
                <button
                  type="button"
                  key={path}
                  onClick={() => goTo(path, protectedPage)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-semibold ${
                    active ? "bg-white text-violet-700" : "text-white hover:bg-violet-600"
                  }`}
                >
                  <Icon size={20} />
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      <div className="fixed top-[72px] left-0 right-0 z-40 bg-[#f8f7ff]/95 px-4 py-2 backdrop-blur">
        <div className="mx-auto max-w-7xl">
          <div className="relative">
            <div className="w-full bg-white rounded-2xl border border-gray-200 px-4 py-3.5 flex items-center gap-3 text-gray-400 shadow-sm focus-within:border-violet-300 transition">
              <Search size={21} className="shrink-0" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search products"
                className="w-full bg-transparent text-gray-900 outline-none placeholder:text-gray-400"
              />
              {searching && <span className="text-xs text-violet-600">Searching...</span>}
            </div>
            {search.trim() && (
              <div className="absolute left-0 right-0 mt-2 max-h-[60vh] overflow-y-auto rounded-2xl border border-gray-100 bg-white p-2 shadow-xl">
                {subcategoryResults.map((item) => (
                  <button
                    type="button"
                    key={`subcategory-${item._id || item.id}`}
                    onClick={() => {
                      setSearch("");
                      setSearchResults([]);
                      setSubcategoryResults([]);
                      navigate(`/productList?subcategoryId=${encodeURIComponent(item._id || item.id)}&subcategory=${encodeURIComponent(item.name || "")}`);
                    }}
                    className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-violet-50"
                  >
                    {item.image && <img src={item.image} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-gray-900">{item.name}</div>
                      <div className="text-xs font-medium text-violet-600">View product list</div>
                    </div>
                  </button>
                ))}
                {searchResults.length ? searchResults.map((product) => (
                  <button
                    type="button"
                    key={product._id || product.id}
                    onClick={() => {
                      setSearch("");
                      setSearchResults([]);
                      navigate(`/product/${product._id || product.id}`);
                    }}
                    className="flex w-full items-center gap-3 rounded-xl p-2 text-left hover:bg-violet-50"
                  >
                    {product.image && <img src={product.image} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-gray-900">{product.name}</div>
                      {product.price != null && <div className="text-sm font-semibold text-violet-700">₹{product.price}</div>}
                    </div>
                  </button>
                )) : !searching && subcategoryResults.length === 0 ? (
                  <div className="px-3 py-5 text-center text-sm text-gray-500">No products found</div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <a
        href={`https://wa.me/918050515079?text=${encodeURIComponent("Hello, I need help with my order.")}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with FrendRops on WhatsApp"
        className="fixed right-4 bottom-20 md:bottom-6 z-[60] flex h-[58px] w-[58px] items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl transition hover:scale-105"
      >
        <svg viewBox="0 0 32 32" aria-hidden="true" className="h-8 w-8 fill-current">
          <path d="M16.04 3C9.42 3 4.03 8.35 4.03 14.94c0 2.32.68 4.59 1.96 6.52L4 28.73l7.46-1.95a12.05 12.05 0 0 0 4.58.89h.01c6.61 0 12-5.35 12-11.94C28.05 9.14 22.66 3 16.04 3Zm0 22.65h-.01a9.99 9.99 0 0 1-5.08-1.39l-.36-.21-4.43 1.16 1.18-4.3-.24-.37a9.86 9.86 0 0 1-1.53-5.27c0-5.48 4.48-9.94 10-9.94 5.51 0 10 4.46 10 9.94 0 5.48-4.49 10.38-9.53 10.38Zm5.48-7.45c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.47a9.03 9.03 0 0 1-1.66-2.06c-.17-.3-.02-.46.13-.61.14-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.68-1.63-.93-2.23-.24-.59-.49-.51-.67-.52h-.57c-.2 0-.52.07-.8.37-.27.3-1.05 1.02-1.05 2.48 0 1.46 1.08 2.87 1.23 3.07.15.2 2.12 3.22 5.13 4.52.72.31 1.28.49 1.72.63.72.23 1.37.2 1.89.12.58-.09 1.77-.72 2.02-1.41.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z"/>
        </svg>
      </a>

      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-violet-100 shadow-[0_-4px_14px_rgba(0,0,0,0.08)]">
        <div className="grid grid-cols-6">
          {customerLinks.map(({ label, path, icon: Icon, protected: protectedPage }) => {
            const active = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
            return (
              <button
                type="button"
                key={path}
                onClick={() => goTo(path, protectedPage)}
                className={`py-2 flex flex-col items-center gap-1 text-[10px] font-semibold ${
                  active ? "text-violet-700" : "text-gray-500"
                }`}
              >
                <Icon size={21} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
